import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { handleLoggingIn, LoginPage } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  FEATURE_FLAGS,
  selectIsFeatureFlagEnabled,
} from '@backrs_ui/grow-ui/src/redux/featureFlags/featureFlags';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { postAPISurveyResults } from '@backrs_ui/grow-ui/src/redux/userResponse/thunks/userResponse.thunk';
import { ENVIRONMENT_URL, STYTCH_HOST, STYTCH_PUBLIC_TOKEN } from '@backrs_ui/grow-ui/src/settings';
import { useDemoAwareRoute } from '@backrs_ui/grow-ui/src/utils/demoUtils';
import { handleAfterLogin } from '@backrs_ui/grow-ui/src/utils/loginHelper';
import { consumePostLoginRedirect } from '@backrs_ui/grow-ui/src/utils/postLoginRedirect';
import { getGuestTokenIfPresent, getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { usePostHog } from 'posthog-js/react';

const LOGIN_STRINGS = appStrings.modules.login;

export const LoginPageContainer = () => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const postHog = usePostHog();
  const myBlueprintRoute = useDemoAwareRoute(APP_ROUTE.MY_BLUEPRINT);
  const isOAuthEnabled = useAppSelector(
    selectIsFeatureFlagEnabled(FEATURE_FLAGS.OAUTH_SHOW_GROW_UI_CONTROLS),
  );

  // honor a redirect target captured by ProtectedRoute (e.g. shared blueprint link).
  // location.state is session-memory only, so refreshing /login drops `from` and the
  // user lands on HOME after login — accepted tradeoff (no sessionStorage fallback).
  const redirectFrom = (location.state as { from?: string } | null)?.from;

  const { user } = useAppSelector((state) => state.user);
  const { careerQuestions } = useAppSelector((state) => state.survey);
  const { careerPaths, careerPathRoles, careerPathMeanings, careerPathEnjoyments } = useAppSelector(
    (state) => state.careerPath,
  );
  const mostRecentUserResponseSet = useAppSelector(
    (state) => state.userResponseSet.mostRecentUserResponseSet,
  );

  useEffect(() => {
    const accessToken = getBackrsToken();

    if (user && accessToken) {
      navigate(consumePostLoginRedirect() ?? redirectFrom ?? APP_ROUTE.HOME);
    }
  }, [user, navigate, redirectFrom]);

  const afterLogin = async (loginResponse: {
    access_token?: string | undefined;
    status_code?: number | undefined;
    phone?: string | undefined;
  }) => {
    if (loginResponse?.status_code === 200) {
      navigate(APP_ROUTE.OTP, { state: { phone: loginResponse.phone } });

      return;
    }

    if (loginResponse?.access_token) {
      handleAfterLogin({
        access_token: loginResponse.access_token,
        dispatch,
        setErrorMessage,
        postHog,
      });

      // skip the post-survey redirect to MY_BLUEPRINT when a destination was preserved
      // (e.g. shared blueprint link) so the useEffect's navigate to redirectFrom wins
      if (mostRecentUserResponseSet && !redirectFrom) {
        // then we know they skipped sign in, but decided to login after taking the survey
        // so we need to post the results with the thunk and then navigate to the results page

        const resultAction = await dispatch(
          postAPISurveyResults({
            userResponseSet: mostRecentUserResponseSet,
            careerQuestions,
            careerPaths,
            careerPathRoles,
            careerPathMeanings,
            careerPathEnjoyments,
          }),
        );

        if (postAPISurveyResults.fulfilled.match(resultAction)) {
          navigate(myBlueprintRoute);

          return;
        }
      }
    }
  };

  const onLogin = async (phone: string, email: string, password: string) => {
    await handleLoggingIn({
      phone,
      email,
      password,
      setErrorMessage,
      environmentUrl: ENVIRONMENT_URL,
      afterLogin,
      guestToken: getGuestTokenIfPresent() ?? undefined,
    });
  };

  return (
    <LoginPage
      title={LOGIN_STRINGS.title}
      handleLoggingIn={onLogin}
      errorMessage={errorMessage}
      environmentUrl={ENVIRONMENT_URL}
      source="grow"
      handleSigningUp={() => navigate(APP_ROUTE.SIGN_UP)}
      oauth={
        isOAuthEnabled
          ? { stytchHost: STYTCH_HOST, stytchPublicToken: STYTCH_PUBLIC_TOKEN }
          : undefined
      }
    />
  );
};
